import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Drawer, Modal } from '@systemeio/components'
import { Lock01Icon, ArrowNarrowRightIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import { COMMUNITY_ACCESS_DENIED_EVENT } from 'shared/constants'
import { AccessDeniedReasonsEnum } from 'shared/errors/access-denied'
import { useIsMobileWhileOpen } from 'shared/hooks/use-is-mobile-while-open'

interface CommunityAccessDeniedEvent extends CustomEvent {
  detail: { reason?: string }
}

export function CommunityOwnerBlockedModal(): ReactNode {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useIsMobileWhileOpen(isOpen)

  useEffect(() => {
    const handleAccessDenied = (event: Event): void => {
      const customEvent = event as CommunityAccessDeniedEvent
      if (customEvent.detail?.reason === AccessDeniedReasonsEnum.CommunityOwnerBlocked) {
        setIsOpen(true)
      }
    }

    globalThis.addEventListener(COMMUNITY_ACCESS_DENIED_EVENT, handleAccessDenied)

    return () => {
      globalThis.removeEventListener(COMMUNITY_ACCESS_DENIED_EVENT, handleAccessDenied)
    }
  }, [])

  const title = t('community.owner_blocked_modal.title')

  const icon = (
    <div className="mb-6 rounded-full bg-THEME-disabled p-5">
      <Lock01Icon className="size-12 text-text-tertiary" />
    </div>
  )

  const description = t('community.owner_blocked_modal.description')

  const action = (
    <a
      href="/community"
      className="group inline-flex items-center gap-2 rounded-md border border-transparent bg-bg-accent-active px-6 py-3 font-medium text-text-on transition-colors duration-300 hover:border hover:border-border-accent hover:bg-bg-primary hover:text-text-link"
    >
      {t('community.owner_blocked_modal.explore_button')}
      <ArrowNarrowRightIcon className="size-5 -rotate-45 text-text-on transition-colors group-hover:text-text-link" />
    </a>
  )

  if (!isMobile) {
    return (
      <Modal.Root open={isOpen} onOpenChange={() => {}}>
        <Modal.Popup>
          <div className="relative flex flex-col items-center px-6 py-10 text-center lg:px-16 lg:py-14">
            {icon}
            <h2 className="mb-3 text-xl font-semibold text-text-primary">{title}</h2>
            <p className="mb-8 text-text-secondary">{description}</p>
            {action}
          </div>
        </Modal.Popup>
      </Modal.Root>
    )
  }

  return (
    <Drawer.Root open={isOpen} onOpenChange={() => {}}>
      <Drawer.Popup>
        <Drawer.Header>
          <Drawer.Title>{title}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Content className="flex flex-col items-center gap-6 text-center">
          {icon}
          <p className="text-text-secondary">{description}</p>
        </Drawer.Content>
        <Drawer.Footer>{action}</Drawer.Footer>
      </Drawer.Popup>
    </Drawer.Root>
  )
}
